import { useState } from "react";
import {
  Box,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import {
  Close,
  Download,
  InsertDriveFileOutlined,
  NavigateBefore,
  NavigateNext,
  OpenInNew,
  PictureAsPdf,
  ZoomIn,
} from "@mui/icons-material";
import { getUploadBaseUrl } from "../../process/processSlices";
import { WallAttachment } from "../types/wallTypes";

const fileUrl = (a: WallAttachment) => `${getUploadBaseUrl()}/${a.url}`;

const isImage = (a: WallAttachment) =>
  a.contentType?.startsWith("image/") ||
  /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(a.url);

const isPdf = (a: WallAttachment) =>
  a.contentType === "application/pdf" || /\.pdf$/i.test(a.url);

const displayName = (a: WallAttachment) =>
  a.name || a.url.split("/").pop() || "file";

/** Force a download instead of navigating, keeping the original file name. */
function downloadFile(a: WallAttachment) {
  const link = document.createElement("a");
  link.href = fileUrl(a);
  link.download = displayName(a);
  link.target = "_blank";
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
}

type Props = {
  attachments?: WallAttachment[];
  /** Smaller thumbnails for comments. */
  dense?: boolean;
};

export default function AttachmentGrid({ attachments, dense }: Props) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  if (!attachments?.length) return null;

  const images = attachments.filter(isImage);
  const files = attachments.filter((a) => !isImage(a));
  const thumb = dense ? 72 : 104;

  const showPrev = () =>
    setLightboxIndex((i) =>
      i === null ? i : (i - 1 + images.length) % images.length,
    );
  const showNext = () =>
    setLightboxIndex((i) => (i === null ? i : (i + 1) % images.length));

  const active = lightboxIndex !== null ? images[lightboxIndex] : null;

  return (
    <Box sx={{ mt: 0.5 }}>
      {images.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            mb: files.length ? 1 : 0,
          }}
        >
          {images.map((a, i) => (
            <Box
              key={a.url}
              onClick={() => setLightboxIndex(i)}
              sx={{
                position: "relative",
                width: thumb,
                height: thumb,
                borderRadius: 2,
                overflow: "hidden",
                cursor: "pointer",
                border: "1px solid",
                borderColor: "divider",
                "&:hover .att-overlay": { opacity: 1 },
              }}
            >
              <Box
                component="img"
                src={fileUrl(a)}
                alt={displayName(a)}
                loading="lazy"
                sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
              <Box
                className="att-overlay"
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(0,0,0,0.35)",
                  color: "#fff",
                  opacity: 0,
                  transition: "opacity 0.2s",
                }}
              >
                <ZoomIn fontSize={dense ? "small" : "medium"} />
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {files.length > 0 && (
        <Stack spacing={0.75}>
          {files.map((a) => (
            <Stack
              key={a.url}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                px: 1.25,
                py: 0.75,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "action.hover",
                maxWidth: 360,
              }}
            >
              {isPdf(a) ? (
                <PictureAsPdf color="error" fontSize="small" />
              ) : (
                <InsertDriveFileOutlined color="action" fontSize="small" />
              )}
              <Typography
                variant="body2"
                noWrap
                sx={{ flex: 1, minWidth: 0 }}
                title={displayName(a)}
              >
                {displayName(a)}
              </Typography>
              <IconButton
                size="small"
                component="a"
                href={fileUrl(a)}
                target="_blank"
                rel="noopener noreferrer"
                title="View"
              >
                <OpenInNew fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => downloadFile(a)}
                title="Download"
              >
                <Download fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      )}

      <Dialog
        open={!!active}
        onClose={() => setLightboxIndex(null)}
        maxWidth="lg"
        PaperProps={{
          sx: { bgcolor: "rgba(0,0,0,0.92)", boxShadow: "none", m: 1 },
        }}
      >
        {active && (
          <Box sx={{ position: "relative" }}>
            <Box
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 2,
                display: "flex",
                gap: 0.5,
              }}
            >
              <IconButton
                onClick={() => downloadFile(active)}
                sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.12)" }}
                title="Download"
              >
                <Download />
              </IconButton>
              <IconButton
                onClick={() => setLightboxIndex(null)}
                sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.12)" }}
                title="Close"
              >
                <Close />
              </IconButton>
            </Box>

            {images.length > 1 && (
              <>
                <IconButton
                  onClick={showPrev}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: 8,
                    transform: "translateY(-50%)",
                    color: "#fff",
                    bgcolor: "rgba(255,255,255,0.12)",
                  }}
                >
                  <NavigateBefore />
                </IconButton>
                <IconButton
                  onClick={showNext}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    right: 8,
                    transform: "translateY(-50%)",
                    color: "#fff",
                    bgcolor: "rgba(255,255,255,0.12)",
                  }}
                >
                  <NavigateNext />
                </IconButton>
              </>
            )}

            <Box
              component="img"
              src={fileUrl(active)}
              alt={displayName(active)}
              sx={{
                display: "block",
                maxWidth: "90vw",
                maxHeight: "85vh",
                objectFit: "contain",
                m: "auto",
              }}
            />
          </Box>
        )}
      </Dialog>
    </Box>
  );
}
